import { parseUnits } from 'viem';

/**
 * Generates an EIP-681 URI for Ethereum-based payments
 * @see https://eips.ethereum.org/EIPS/eip-681
 */
export function generateEIP681URI(params: {
  address: string;
  amount?: string;
  amountBaseUnits?: string;
  chainId: number;
  tokenAddress?: string;
  decimals?: number;
}): string {
  const { address, amount, amountBaseUnits, chainId, tokenAddress, decimals = 18 } = params;
  
  if (tokenAddress) {
    // ERC-20 transfer with optional amount
    const uint256 = amountBaseUnits ?? (amount ? parseUnits(amount, decimals).toString() : undefined);
    const base = `ethereum:${tokenAddress}@${chainId}/transfer?address=${address}`;
    return uint256 ? `${base}&uint256=${uint256}` : base;
  } else {
    // Native transfer with optional value
    const value = amountBaseUnits ?? (amount ? parseUnits(amount, 18).toString() : undefined);
    const base = `ethereum:${address}@${chainId}`;
    return value ? `${base}?value=${value}` : base;
  }
}

/**
 * Generates a Solana Pay URL
 * @see https://docs.solanapay.com/spec
 */
export function generateSolanaPayURL(params: {
  recipient: string;
  amount?: number;
  splToken?: string;
  reference?: string;
  label?: string;
  message?: string;
}): string {
  const url = new URL(`solana:${params.recipient}`);
  
  // Add amount parameter
  if (typeof params.amount === 'number') {
    url.searchParams.append('amount', params.amount.toString());
  }
  
  // Add optional parameters
  if (params.splToken) {
    url.searchParams.append('spl-token', params.splToken);
  }
  if (params.reference) {
    url.searchParams.append('reference', params.reference);
  }
  if (params.label) {
    url.searchParams.append('label', params.label);
  }
  if (params.message) {
    url.searchParams.append('message', params.message);
  }
  
  return url.toString();
}

/**
 * Generates a BIP-21 URI for Bitcoin payments
 * @see https://github.com/bitcoin/bips/blob/master/bip-0021.mediawiki
 */
export function generateBIP21URI(params: {
  address: string;
  amount?: number;
  label?: string;
  message?: string;
}): string {
  let uri = `bitcoin:${params.address}`;
  
  const queryParams: string[] = [];
  
  // Add amount in BTC
  if (typeof params.amount === 'number') {
    queryParams.push(`amount=${params.amount}`);
  }
  
  // Add optional parameters
  if (params.label) {
    queryParams.push(`label=${encodeURIComponent(params.label)}`);
  }
  if (params.message) {
    queryParams.push(`message=${encodeURIComponent(params.message)}`);
  }
  
  if (queryParams.length > 0) {
    uri += '?' + queryParams.join('&');
  }
  
  return uri;
}

/**
 * Generates a generic payment URI for networks without specific standards
 */
export function generateGenericURI(params: {
  network: string;
  address: string;
  amount?: string;
  token?: string;
}): string {
  // For Tron, Polkadot, and other networks without standard URI schemes
  // Generate a custom URI that can be displayed as QR code
  const data = {
    network: params.network,
    address: params.address,
    amount: params.amount,
    token: params.token
  };
  
  // Return as JSON for QR code generation
  return JSON.stringify(data);
}

/**
 * Main function to generate payment URI based on network
 */
export function generatePaymentURI(params: {
  network: string;
  address: string;
  amount?: string;
  amountBaseUnits?: string;
  chainId?: number;
  tokenAddress?: string;
  decimals?: number;
  label?: string;
  message?: string;
}): string | null {
  const { network, address, amount, amountBaseUnits, chainId, tokenAddress, decimals, label, message } = params;
  
  switch (network) {
    case 'Ethereum':
    case 'Arbitrum':
    case 'Base':
    case 'Polygon':
    case 'BNB':
    case 'Avalanche':
      if (!chainId) return null;
      return generateEIP681URI({
        address,
        amount,
        amountBaseUnits,
        chainId,
        tokenAddress,
        decimals
      });
      
    case 'Solana':
      return generateSolanaPayURL({
        recipient: address,
        amount: amount ? parseFloat(amount) : undefined,
        splToken: tokenAddress,
        label: label || 'Token Presale',
        message: message || 'Purchase tokens'
      });
      
    case 'Bitcoin':
      return generateBIP21URI({
        address,
        amount: amount ? parseFloat(amount) : undefined,
        label: label || 'Token Presale',
        message: message || 'Purchase tokens'
      });
      
    case 'Tron':
    case 'Polkadot':
      // These networks don't have standard URI schemes
      return generateGenericURI({
        network,
        address,
        amount,
        token: tokenAddress
      });
      
    default:
      return null;
  }
}

/**
 * Parses a payment URI and extracts relevant information
 */
export function parsePaymentURI(uri: string): {
  network?: string;
  address?: string;
  amount?: string;
  tokenAddress?: string;
  chainId?: number;
} | null {
  try {
    // Check for Ethereum-style URI
    if (uri.startsWith('ethereum:')) {
      const match = uri.match(/ethereum:([^@\?]+)(?:@(\d+))?(?:\/transfer)?\?(.+)/);
      if (!match) return null;
      
      const [, addressOrToken, chainId, params] = match;
      const searchParams = new URLSearchParams(params);
      
      if (uri.includes('/transfer')) {
        // ERC-20 transfer
        return {
          network: 'Ethereum',
          address: searchParams.get('address') || undefined,
          amount: searchParams.get('uint256') || undefined,
          tokenAddress: addressOrToken,
          chainId: chainId ? parseInt(chainId) : undefined
        };
      } else {
        // Native transfer
        return {
          network: 'Ethereum',
          address: addressOrToken,
          amount: searchParams.get('value') || undefined,
          chainId: chainId ? parseInt(chainId) : undefined
        };
      }
    }
    
    // Check for Solana URI
    if (uri.startsWith('solana:')) {
      const url = new URL(uri);
      return {
        network: 'Solana',
        address: url.pathname.replace('/', ''),
        amount: url.searchParams.get('amount') || undefined,
        tokenAddress: url.searchParams.get('spl-token') || undefined
      };
    }
    
    // Check for Bitcoin URI
    if (uri.startsWith('bitcoin:')) {
      const url = new URL(uri);
      return {
        network: 'Bitcoin',
        address: url.pathname.replace('/', ''),
        amount: url.searchParams.get('amount') || undefined
      };
    }
    
    // Try to parse as JSON (generic format)
    try {
      const data = JSON.parse(uri);
      if (data.network && data.address) {
        return {
          network: data.network,
          address: data.address,
          amount: data.amount,
          tokenAddress: data.token
        };
      }
    } catch {
      // Not JSON
    }
    
    return null;
  } catch (error) {
    console.error('Failed to parse payment URI:', error);
    return null;
  }
}